// #region imports
// Internalized from @videoplatform/ts-freezer (freezer module). Uses the local
// withInjectedContext + freezer.types augmentation.
import "./freezer.types";
import Freezer = require('freezer-js');
import * as React from "react";
import { ReactComponentLike } from "./withInjectedContext";
export { Freezer };
import { bind } from "bind-decorator";
// #endregion imports

export interface ISubscribableToUpdates {
    subscribeToUpdates: (handler: () => void) => void;
    unsubscribeFromUpdates: (handler: () => void) => void;
}

type SubnodeSelector<DataType> = (root: Freezer.Types.IFrozen<DataType>) => Freezer.Types.IFreezerNodeListener<DataType, {}>;

type IInjectionOptions<DataType> = {
    subscribeToUpdates: boolean;
    onlyListenToSubNode?: SubnodeSelector<DataType>;
}

type ConvertToServiceProps<ServiceType, InjectedPropName extends string> = {[name in InjectedPropName]: ServiceType};

export class FreezerService<DataType, InjectedPropName extends string = "service", EventTypes = {}> implements ISubscribableToUpdates {
    // @ts-ignore
    public freezer: Freezer.Types.IFreezer<DataType>;
    public Context: React.Context<this>

    constructor(initialState: DataType, private injectedPropName: InjectedPropName) {
        // @ts-ignore
        this.freezer = new Freezer(initialState);

        // @ts-ignore // TODO: This fails type checking in Jest tests for some reason
        this.on = this.freezer.on.bind(this.freezer);
        // @ts-ignore // TODO: This fails type checking in Jest tests for some reason
        this.once = this.freezer.once.bind(this.freezer);
        // @ts-ignore // TODO: This fails type checking in Jest tests for some reason
        this.off = this.freezer.off.bind(this.freezer);
        // @ts-ignore // TODO: This fails type checking in Jest tests for some reason
        this.emit = this.freezer.emit.bind(this.freezer);

        this.Context = React.createContext(this);
    }

    // Expose the freezer's IListener interface
    on: typeof Freezer.on;
    once: typeof Freezer.once;
    off: typeof Freezer.off;
    emit: typeof Freezer.emit;

    subscribeToUpdates(handler: () => void) {
        this.freezer.on('update', handler);
    }

    unsubscribeFromUpdates(handler: () => void) {
        this.freezer.off('update', handler);
    }

    /**
     * Uses the HOC pattern to wrap a component and inject this service into a component class.
     * @param Component The original Component to wrap
     */
    inject<IOriginalProps extends Object = {}>(Component: ReactComponentLike<IOriginalProps & ConvertToServiceProps<this, InjectedPropName>>): React.FunctionComponent<IOriginalProps> {
        return this._inject({
            subscribeToUpdates: true,
        }, Component);
    }

    injectButDontSubscribeToUpdates<IOriginalProps extends Object = {}>(Component: ReactComponentLike<IOriginalProps & ConvertToServiceProps<this, InjectedPropName>>): React.FunctionComponent<IOriginalProps> {
        return this._inject({
            subscribeToUpdates: false,
        }, Component);
    }

    injectButOnlySubscribeToSubnode<IOriginalProps extends Object = {}>(subnodeSelector: SubnodeSelector<DataType>, Component: ReactComponentLike<IOriginalProps & ConvertToServiceProps<this, InjectedPropName>>): React.FunctionComponent<IOriginalProps> {
        return this._inject({
            subscribeToUpdates: true,
            onlyListenToSubNode: subnodeSelector,
        }, Component);
    }

    private _inject<IOriginalProps extends Object = {}>(options: IInjectionOptions<DataType>, Component: ReactComponentLike<IOriginalProps & ConvertToServiceProps<this, InjectedPropName>>): React.FunctionComponent<IOriginalProps> {
        return withInjectedService<IOriginalProps, DataType, InjectedPropName, EventTypes, this, ConvertToServiceProps<this, InjectedPropName>>(
            Component,
            this,
            options
        );
    }

    /**
     * You shouldn't really call this directly.  This should just be used internally in the service provider, and for
     * services to expose their injection prop types:
     *
     *      export type I...ServiceInjectedProps= ReturnType<...FreezerService["getPropsForInjection"]>
     *
     * Otherwise, it is called by inject().  It would be 'internal', if TS supported that access modifier, but they
     * don't.
     */
    getPropsForInjection(): ConvertToServiceProps<this, InjectedPropName> {
        // This is safe, but we have to go through unknown so that TS knows that.  It seems to get choked on the
        // fact that we're using this in the generic, and so it can't confirm that it's the right type, but simple
        // inspection will reveal that there's no way for it to be wrong.  Somehow this.injectedPropName's type
        // gets widened back to string (from a string literal) when using this syntax.
        //
        // @ts-ignore
        let props: ConvertToServiceProps<this, InjectedPropName> = {
            [this.injectedPropName]: this,
        };

        return props;
    }
}

/**
 * HOC to wrap a component with a service in a way that will update the component when the service updates.
 * @param Component
 * @param service
 */
export function withInjectedService<
    IOriginalProps extends Object,
    DataType,
    InjectedPropName extends string,
    EventTypes,
    Service extends FreezerService<DataType, InjectedPropName, EventTypes>,
    IPropsWithService = ConvertToServiceProps<Service, InjectedPropName>
>(
    Component: ReactComponentLike<IOriginalProps & IPropsWithService>,
    service: Service,
    options: IInjectionOptions<DataType>
): React.FunctionComponent<IOriginalProps> {
    return (props: IOriginalProps) => {
        const generateBody = () => {
            let injectedProps = service.getPropsForInjection();

            let combinedProps = {
                ...props,
                ...injectedProps,
            }

            // @ts-ignore:2322 // https://github.com/Microsoft/TypeScript/issues/19139
            let body = <Component {...combinedProps} />

            return body;
        };

        if (options.subscribeToUpdates) {
            let listener: Freezer.Types.IFreezerNodeListener<any, any> | undefined;
            if (options.onlyListenToSubNode) {
                listener = options.onlyListenToSubNode(service.freezer.get());
            }

            return (
                <WithSubscribableUpdaterHOC.Component
                    service={service}
                    listener={listener}
                    generateChildren={generateBody}
                />
            )
        }
        else {
            return generateBody();
        }
    }
}

namespace WithSubscribableUpdaterHOC {
    export interface IProps<Service extends ISubscribableToUpdates> {
        service: Service;
        listener?: Freezer.Types.IFreezerNodeListener<any, any>;
        generateChildren: () => React.ReactNode;
    }

    export class Component<Service extends ISubscribableToUpdates> extends React.Component<IProps<Service>> {
        constructor(props: IProps<Service>) {
            super(props);
        }

        componentDidMount() {
            if (this.props.listener) {
                this.props.listener.on('update', this.refresh);
            }
            else {
                this.props.service.subscribeToUpdates(this.refresh);
            }
        }

        componentWillUnmount() {
            if (this.props.listener) {
                this.props.listener.off('update', this.refresh);
            }
            else {
                this.props.service.unsubscribeFromUpdates(this.refresh);
            }
        }

        @bind
        refresh() {
            this.forceUpdate();
        }


        render() {
            return (
                <>
                    {this.props.generateChildren()}
                </>
            )
        }
    }
}
